import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ButtonRadiusEnum } from '../../shared/components/button/types/button-radius.enum';
import { ButtonSizeEnum } from '../../shared/components/button/types/button-size.enum';
import { ButtonTypeEnum } from '../../shared/components/button/types/button-type.enum';
import { InputComponent } from '../../shared/components/input/input.component';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { CategoryTableComponent } from '../../shared/components/category-table/category-table.component';
import { Sorts } from '../../shared/services/pagination/models/typies/sorts.type';
import { ModalAction } from '../../shared/modals/modal-actions/modal-action.interface';
import { PaginationDto } from '../../shared/services/pagination/models/interfaces/pagination-dto';
import { CrudRequestParams } from '../../shared/services/pagination/models/interfaces/crud';
import { ModalService } from '../../shared/services/modal/modal.service';
import { createCancelConfirmButtons, openConfirmModal } from '../../helpers/modal.helper';
import { Category } from '../../shared/models/interfaces/category.interface';
import { CategoryService } from '../../shared/services/category/category.service';
import { CreateCategoryModalComponent } from './modals/create-category-modal.component';
import { SuccessModalComponent } from '../../shared/modals/success-modal/success-modal.component';
import { ToastService } from '../../shared/components/toast/service/toast.service';
import { getCrudErrorMessage } from '../../shared/utils/functions';

@Component({
  selector: 'l-category',
  standalone: true,
  imports: [
    IconComponent,
    ButtonComponent,
    InputComponent,
    ReactiveFormsModule,
    CategoryTableComponent,
  ],
  templateUrl: './category.component.html',
})
export class CategoryComponent implements OnInit, OnDestroy {
  constructor(
    private readonly categoryService: CategoryService,
    private readonly modalService: ModalService,
    private readonly toastService: ToastService,
  ) {}

  public readonly buttonTypeEnum = ButtonTypeEnum;

  public readonly buttonSizeEnum = ButtonSizeEnum;

  public readonly buttonRadiusEnum = ButtonRadiusEnum;

  public readonly searchControl = new FormControl('');

  public isLoading = false;

  public limit = 10;

  public page = 1;

  public sort: Sorts = { field: 'updatedAt', order: 'DESC' };

  public source: PaginationDto<Category> = {
    data: [],
    page: 1,
    pageCount: 0,
    count: 0,
    total: 0,
  };

  private readonly destroy$$ = new Subject<void>();

  private readonly modalWidth = '700px';

  public get categories(): Category[] {
    return this.source.data;
  }

  public get total(): number {
    return this.source.total;
  }

  public async ngOnInit(): Promise<void> {
    this.searchControl.valueChanges.pipe(
      debounceTime(400),
      takeUntil(this.destroy$$),
    ).subscribe((value) => {
      void this.loadCategories(1, value || '');
    });

    await this.loadCategories(1);
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  public openCreateCategoryModal(): void {
    const ref = this.modalService.open(CreateCategoryModalComponent, {
      width: this.modalWidth,
      modal: true,
      closable: false,
      showHeader: false,
      data: {
        isUpdate: false,
      },
    });

    ref.onClose.subscribe((result: boolean | undefined) => {
      if (result !== true) {
        return;
      }

      void this.handleCategorySaved('create');
    });
  }

  public async onChangePage(page: number): Promise<void> {
    await this.loadCategories(page);
  }

  public async onChangeSort(sort?: Sorts): Promise<void> {
    this.sort = sort || { field: 'updatedAt', order: 'DESC' };
    await this.loadCategories(1);
  }

  public getRowActions(category: Category): ModalAction[] {
    return [
      {
        label: 'Editar',
        icon: 'edit',
        callback: () => this.openEditCategoryModal(category),
      },
      {
        label: 'Excluir',
        icon: 'delete',
        colorClass: 'text-lDanger',
        callback: () => this.openCategoryDeleteModal(category),
      },
    ];
  }

  private async loadCategories(page: number, search?: string): Promise<void> {
    try {
      this.isLoading = true;

      const searchValue = (search ?? this.searchControl.value ?? '').trim();

      const params: CrudRequestParams<Category> = {
        page,
        limit: this.limit,
        sort: [{ field: this.sort.field, order: this.sort.order }],
      };

      if (searchValue) {
        params.search = {
          $or: [
            { name: { $contL: searchValue } },
            { description: { $contL: searchValue } },
          ],
        };
      }

      this.source = await this.categoryService.getMany(params);
      this.page = this.source.page;
    } finally {
      this.isLoading = false;
    }
  }

  private openCategoryDeleteModal(category: Category): void {
    openConfirmModal(
      this.modalService,
      this.modalWidth,
      {
        title: 'Excluir categoria',
        description: `Tem certeza que deseja excluir a categoria "${category.name}"?`,
        buttons: createCancelConfirmButtons('Excluir', ButtonTypeEnum.DANGER),
      },
      async () => {
        await this.deleteCategory(category);
      },
      this.destroy$$,
    );
  }

  private async deleteCategory(category: Category): Promise<void> {
    try {
      await this.categoryService.delete(category.id);
      await this.loadCategories(this.page);
      this.toastService.success('Categoria excluída com sucesso.');
    } catch (error) {
      this.toastService.error(getCrudErrorMessage(error), 'Oops...');
    }
  }

  private openEditCategoryModal(category: Category): void {
    const ref = this.modalService.open(CreateCategoryModalComponent, {
      width: this.modalWidth,
      modal: true,
      closable: false,
      showHeader: false,
      data: {
        isUpdate: true,
        category,
      },
    });

    ref.onClose.subscribe((result: boolean | undefined) => {
      if (result !== true) {
        return;
      }

      void this.handleCategorySaved('edit');
    });
  }

  private async handleCategorySaved(mode: 'create' | 'edit'): Promise<void> {
    await this.loadCategories(this.page);

    this.modalService.open(SuccessModalComponent, {
      width: this.modalWidth,
      modal: true,
      closable: false,
      showHeader: false,
      data: {
        title: mode === 'create' ? 'Categoria criada com sucesso!' : 'Categoria editada com sucesso!',
        subtitle: 'Você pode visualizar ou editar através da lista de categorias',
        imageAlt: mode === 'create' ? 'Categoria criada com sucesso' : 'Categoria editada com sucesso',
      },
    });
  }
}
